import React, { useState, useEffect } from 'react';
import Filters from '../components/reviews/Filters';
import ReviewList from '../components/reviews/ReviewList';

// Import the bank data from context
import { useData } from '../contexts/DataContext';

const Reviews = () => {
  // Get data from context
  const { data: bankData } = useData();

  const today = new Date();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(today.getDate() - 30);
  
  const [reviews, setReviews] = useState([]);
  const [filteredReviews, setFilteredReviews] = useState([]);
  const [filters, setFilters] = useState({
    source: 'NPS Response',
    region: '',
    city: '', 
    startDate: thirtyDaysAgo,
    endDate: today,
    replyStatus: '',
    sentiment: '',
    category: '',
    customerType: '' // Add customerType filter
  });
  
  // Regions data for filters
  const regions = {
    'North': ['Delhi', 'Chandigarh', 'Jaipur'],
    'South': ['Bangalore', 'Chennai', 'Hyderabad'],
    'East': ['Kolkata', 'Bhubaneswar'],
    'West': ['Mumbai', 'Pune', 'Ahmedabad']
  };
  
  // Load reviews when bankData is available
  useEffect(() => {
    if (!bankData || !bankData.data) return;
    
    try {
      // Process the bank data to match our review format
      const processedReviews = bankData.data.map((item, index) => {
        // Extract categories from processed_data
        const categorySentiments = item.processed_data?.categories?.map(cat => ({
          category: cat.category.replace('_', ' ').charAt(0).toUpperCase() + cat.category.replace('_', ' ').slice(1),
          sentiment: cat.sentiment.charAt(0).toUpperCase() + cat.sentiment.slice(1)
        })) || [];
        
        // Extract all unique categories for this review
        const reviewCategories = item.processed_data?.categories?.map(cat => 
          cat.category.replace('_', ' ').charAt(0).toUpperCase() + cat.category.replace('_', ' ').slice(1)
        ) || [];
        
        return {
          id: index,
          reviewId: item.reviewId,
          userName: `Customer ${index + 1}`,
          source: 'NPS Response',
          date: new Date().toISOString(), // Use current date as fallback
          rating: item.sentiment === 'Promoter' ? 5 : item.sentiment === 'Passive' ? 3 : 1,
          reviewText: item.text,
          replyStatus: 'Not Replied',
          sentiment: item.sentiment === 'Promoter' ? 'Positive' : 
                     item.sentiment === 'Detractor' ? 'Negative' : 'Neutral',
          region: item.transactingBranch === 'Home' ? 'North' : 'South', // Placeholder
          city: item.transactingBranch === 'Home' ? 'Delhi' : 'Bangalore', // Placeholder
          categorySentiments,
          categories: reviewCategories,
          productOwnership: item["Product ownership"] || "N/A",
          transactingBranch: item.transactingBranch,
          customerType: item.customerType || '', // Add customerType from original data
          processed_data: item.processed_data || {} // Pass the original processed_data
        };
      });
      
      setReviews(processedReviews);
      setFilteredReviews(processedReviews);
    } catch (error) {
      console.error('Error loading reviews data:', error);
      // Handle error appropriately
    }
  }, [bankData]);
  
  // Extract unique categories from reviews for the filter dropdown
  const availableCategories = React.useMemo(() => {
    const categorySet = new Set();
    reviews.forEach(review => {
      if (review.categories) {
        review.categories.forEach(category => categorySet.add(category));
      }
    });
    return Array.from(categorySet).sort();
  }, [reviews]);

  // Calculate customer type counts from reviews
  const customerTypeCounts = React.useMemo(() => {
    const counts = { managed: 0, unmanaged: 0, total: reviews.length };
    
    reviews.forEach(review => {
      if (review.customerType === 'managed') {
        counts.managed++;
      } else if (review.customerType === 'unmanaged') {
        counts.unmanaged++;
      }
    });
    
    return counts;
  }, [reviews]);
  
  // Apply filters when they change
  useEffect(() => {
    applyFilters();
  }, [filters, reviews]);
  
  const applyFilters = () => {
    let result = [...reviews];
    
    // Filter by source/channel
    if (filters.source) {
      result = result.filter(review => review.source === filters.source);
    }
    
    // Filter by region
    if (filters.region) {
      result = result.filter(review => review.region === filters.region);
    }
    
    // Filter by city
    if (filters.city) {
      result = result.filter(review => review.city === filters.city);
    }
    
    // Filter by date range
    if (filters.startDate) {
      result = result.filter(review => new Date(review.date) >= filters.startDate);
    }
    
    if (filters.endDate) {
      // Add one day to include the end date fully
      const endDatePlus = new Date(filters.endDate);
      endDatePlus.setDate(endDatePlus.getDate() + 1);
      result = result.filter(review => new Date(review.date) <= endDatePlus);
    }
    
    // Filter by reply status
    if (filters.replyStatus) {
      result = result.filter(review => review.replyStatus === filters.replyStatus);
    }
    
    // Filter by sentiment
    if (filters.sentiment) {
      result = result.filter(review => review.sentiment === filters.sentiment);
    }
    
    // Filter by category
    if (filters.category) {
      result = result.filter(review => 
        review.categories && 
        review.categories.length > 0 && 
        review.categories.includes(filters.category)
      );
    }

    // Filter by customerType
    if (filters.customerType) {
      result = result.filter(review => review.customerType === filters.customerType);
    }
    
    // Sort by date (most recent first)
    result.sort((a, b) => new Date(b.date) - new Date(a.date));
    
    setFilteredReviews(result);
  };
  
  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Customer Feedback</h1>
        <p className="text-sm text-gray-600">Monitor and analyze banking customer feedback and NPS data</p>
      </div>
      
      <Filters 
        filters={filters} 
        setFilters={setFilters} 
        regions={regions}
        categories={availableCategories}
        customerTypeCounts={customerTypeCounts}
      />
      
      <ReviewList reviews={filteredReviews} />
    </div>
  );
};

export default Reviews;